import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, patch, post } from "common/helpers/HTTP";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";

import type { Webhook } from "../types";

async function deleteWebhookRequest(workspaceId: string, webhookId: string) {
  await del(`workspaces/${workspaceId}/webhooks/${webhookId}`);
}

async function updateWebhookRequest(
  workspaceId: string,
  webhookId: string,
  updates: Partial<Omit<Webhook, "id">>,
) {
  await patch(`workspaces/${workspaceId}/webhooks/${webhookId}`, updates);
}

async function createWebhookRequest(
  workspaceId: string,
  newWebhook: Omit<Webhook, "id">,
): Promise<Webhook> {
  return (
    await post(`workspaces/${workspaceId}/webhooks`, {
      ...newWebhook,
      // Note: This is needed for the backend
      integration: false,
    })
  ).webhook;
}

export default function useWebhookMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { webhooksKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: deleteWebhook, isPending: isDeletingWebhook } =
    useMutation({
      mutationFn: ({ webhookId }: { webhookId: string }) =>
        deleteWebhookRequest(workspaceId, webhookId),
      onSuccess: (_, { webhookId }) => {
        queryClient.setQueryData<Webhook[]>(webhooksKeys.list, (prevWebhooks) =>
          prevWebhooks.filter(({ id }) => id !== webhookId),
        );

        toast.success("Webhook deleted");
      },
      onError: () => toast.error("Error deleting Webhook"),
    });

  const { mutateAsync: updateWebhook, isPending: isUpdatingWebhook } =
    useMutation({
      mutationFn: ({
        webhookId,
        updates,
      }: {
        webhookId: string;
        updates: Partial<Omit<Webhook, "id">>;
      }) => updateWebhookRequest(workspaceId, webhookId, updates),
      onError: () => toast.error("Error updating Webhook"),
      onMutate: ({ webhookId, updates }) => {
        queryClient.setQueryData<Webhook[]>(webhooksKeys.list, (prevWebhooks) =>
          prevWebhooks.map((webhook) => {
            if (webhook.id === webhookId) {
              return { ...webhook, ...updates };
            }

            return webhook;
          }),
        );
      },
      onSuccess: () => toast.success("Webhook updated"),
    });

  const { mutateAsync: createWebhook, isPending: isCreatingWebhook } =
    useMutation({
      mutationFn: ({ newWebhook }: { newWebhook: Omit<Webhook, "id"> }) =>
        createWebhookRequest(workspaceId, newWebhook),
      onSuccess: (newWebhook) => {
        queryClient.setQueryData<Webhook[]>(
          webhooksKeys.list,
          (prevWebhooks) => [newWebhook, ...prevWebhooks],
        );

        toast.success("Webhook created");
      },
      onError: () => toast.error("Error creating Webhook"),
    });

  return {
    deleteWebhook,
    isDeletingWebhook,
    updateWebhook,
    isUpdatingWebhook,
    createWebhook,
    isCreatingWebhook,
  };
}
